/**
 * Painel de detalhe da turma (professor) — conteúdo reutilizado no sheet mobile e no modal desktop.
 */
import {
  Users, BookOpen, Clock, ClipboardList, ChevronRight,
  CheckCircle2, AlertTriangle, Calendar,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { TurmaDetalheData } from './TurmaDetalheSheet';

const TURNO: Record<string, string> = {
  MANHA: 'Manhã',
  TARDE: 'Tarde',
  NOITE: 'Noite',
  INTEGRAL: 'Integral',
};

interface GradeDia {
  key: string;
  label: string;
  aulas: Array<{
    id: string;
    horaInicio?: string;
    horaFim?: string;
    materia?: { nome?: string };
  }>;
}

interface AlunoResumo {
  id: string;
  nome: string;
}

export function ProfessorTurmaPainel({
  turma: t,
  grade,
  diaSemanaHoje,
  alunos,
  loadingAlunos,
  variant = 'sheet',
  onAlunoClick,
  onBeforeChamada,
}: {
  turma: TurmaDetalheData;
  grade: GradeDia[];
  diaSemanaHoje?: string | null;
  alunos: AlunoResumo[];
  loadingAlunos?: boolean;
  variant?: 'sheet' | 'modal' | 'panel';
  /** Desktop: abrir ficha do aluno na lista; mobile: rota dedicada */
  onAlunoClick?: (alunoId: string) => void;
  onBeforeChamada?: () => void;
}) {
  const navigate = useNavigate();
  const isModal = variant === 'modal';
  const isPanel = variant === 'panel';
  const showHero = isModal || isPanel;
  const materiasLista = [...t.materias];
  const diasLista = grade.map((g) => g.label).join(', ');

  const handleAluno = (id: string) => {
    if (onAlunoClick) {
      onAlunoClick(id);
      return;
    }
    navigate(`/professor/alunos/${id}`);
  };

  const gradeBlock = grade.length > 0 && (
    <div className="turma-sheet-section">
      <div className="turma-sheet-section-title">Grade semanal</div>
      <div className="turma-sheet-grade">
        {grade.map((dia) => (
          <div key={dia.key}>
            <div
              className={`turma-sheet-grade-dia ${dia.key === diaSemanaHoje ? 'hoje' : ''}`}
            >
              {dia.label}
              {dia.key === diaSemanaHoje ? ' (hoje)' : ''}
            </div>
            {dia.aulas.map((h) => (
              <div key={h.id} className="turma-sheet-grade-aula">
                <span className="turma-sheet-grade-hora">
                  {h.horaInicio}–{h.horaFim}
                </span>
                <span>{h.materia?.nome || '—'}</span>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );

  const alunosBlock = (
    <div className="turma-sheet-section prof-turma-alunos-block">
      <div className="turma-sheet-section-title">
        Alunos da turma ({loadingAlunos ? '…' : alunos.length})
      </div>
      {loadingAlunos ? (
        <p className="turma-sheet-section-text">Carregando lista...</p>
      ) : alunos.length === 0 ? (
        <p className="turma-sheet-section-text">Nenhum aluno ativo nesta turma.</p>
      ) : (
        <div className="turma-sheet-alunos">
          {alunos.map((a) => (
            <button
              key={a.id}
              type="button"
              className="turma-sheet-aluno-row"
              onClick={() => handleAluno(a.id)}
            >
              <span className="turma-sheet-aluno-iniciais">
                {a.nome
                  ?.split(' ')
                  .slice(0, 2)
                  .map((n) => n[0])
                  .join('')
                  .toUpperCase() || '?'}
              </span>
              <span className="turma-sheet-aluno-nome">{a.nome}</span>
              <ChevronRight size={14} />
            </button>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div
      className={[
        'prof-turma-painel',
        isModal ? 'prof-turma-painel--modal' : '',
        isPanel ? 'prof-turma-painel--panel' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {showHero ? (
        <div className="prof-turma-hero">
          <div className="prof-turma-hero-icon" aria-hidden>
            <Calendar size={22} />
          </div>
          <div className="prof-turma-hero-text">
            <h3 className="prof-turma-hero-name">{t.nome}</h3>
            <p className="prof-turma-hero-meta">
              {[t.serie, t.turno ? TURNO[t.turno] || t.turno : '', t.sala]
                .filter(Boolean)
                .join(' · ')}
            </p>
          </div>
        </div>
      ) : null}

      <div className="turma-sheet-stats">
        <div className="turma-sheet-stat">
          <Users size={14} />
          <span className="turma-sheet-stat-val">{t.totalAlunos}</span>
          <span className="turma-sheet-stat-lbl">Alunos</span>
        </div>
        <div className="turma-sheet-stat">
          <BookOpen size={14} />
          <span className="turma-sheet-stat-val">{materiasLista.length}</span>
          <span className="turma-sheet-stat-lbl">Matérias</span>
        </div>
        <div className="turma-sheet-stat">
          <Calendar size={14} />
          <span className="turma-sheet-stat-val">{t.aulasSemana}</span>
          <span className="turma-sheet-stat-lbl">Aulas/sem.</span>
        </div>
        <div className="turma-sheet-stat">
          <Clock size={14} />
          <span className="turma-sheet-stat-val">{t.aulasHoje}</span>
          <span className="turma-sheet-stat-lbl">Hoje</span>
        </div>
      </div>

      {t.aulasHoje > 0 && (
        <div
          className={`turma-sheet-chamada-banner ${t.chamadaHojeFeita ? 'ok' : 'pend'}`}
        >
          {t.chamadaHojeFeita ? (
            <>
              <CheckCircle2 size={16} />
              <span>
                Chamada de hoje concluída
                {t.chamadaHojeTotal > 0 &&
                  ` — ${t.chamadaHojePres}/${t.chamadaHojeTotal} presentes`}
              </span>
            </>
          ) : (
            <>
              <AlertTriangle size={16} />
              <span>
                Chamada de hoje pendente ({t.aulasHoje} aula{t.aulasHoje > 1 ? 's' : ''})
              </span>
            </>
          )}
        </div>
      )}

      {(isModal || isPanel) && (materiasLista.length > 0 || diasLista) ? (
        <div className="prof-turma-meta-row">
          {materiasLista.length > 0 && (
            <div className="turma-sheet-section prof-turma-meta-cell">
              <div className="turma-sheet-section-title">Matérias que você leciona</div>
              <p className="turma-sheet-section-text">{materiasLista.join(' · ')}</p>
            </div>
          )}
          {diasLista && (
            <div className="turma-sheet-section prof-turma-meta-cell">
              <div className="turma-sheet-section-title">Dias na sua grade</div>
              <p className="turma-sheet-section-text">{diasLista}</p>
            </div>
          )}
        </div>
      ) : (
        <>
          {materiasLista.length > 0 && (
            <div className="turma-sheet-section">
              <div className="turma-sheet-section-title">Matérias que você leciona</div>
              <p className="turma-sheet-section-text">{materiasLista.join(' · ')}</p>
            </div>
          )}
          {diasLista && (
            <div className="turma-sheet-section">
              <div className="turma-sheet-section-title">Dias na sua grade</div>
              <p className="turma-sheet-section-text">{diasLista}</p>
            </div>
          )}
        </>
      )}

      {t.aulasHoje > 0 && !t.chamadaHojeFeita && (
        <button
          type="button"
          className="turma-sheet-btn-chamada"
          onClick={() => {
            onBeforeChamada?.();
            navigate('/professor/chamada');
          }}
        >
          <ClipboardList size={16} />
          Ir para Chamada
        </button>
      )}

      {isModal || isPanel ? (
        <div className="prof-turma-modal-cols">
          {gradeBlock}
          {alunosBlock}
        </div>
      ) : (
        <>
          {gradeBlock}
          {alunosBlock}
        </>
      )}
    </div>
  );
}
